/**
 * Role keyword tracks used for keyword-coverage scoring.
 *
 * `must` keywords are what a credible profile in this track almost always
 * mentions somewhere. Coverage of these is scored.
 * `nice` keywords are bonuses — reported as opportunities, never penalised.
 *
 * Keep entries lowercase. Matching is case-insensitive and whole-word (see
 * `findPhrases` in `../text.ts`), so prefer the base form: `'unit test'` will
 * not match `'unit testing'`, so list both where it matters.
 *
 * Adding a track is a welcome first contribution — see `docs/ADDING_A_RULE.md`.
 * Tracks are ordered by `group`, then alphabetically, for the picker.
 */

export interface RoleTrack {
  id: string;
  label: string;
  /** Grouping label for the role picker. */
  group: string;
  must: readonly string[];
  nice: readonly string[];
}

export const ROLE_TRACKS: readonly RoleTrack[] = [
  /* ---------------------------- Engineering ---------------------------- */
  {
    id: 'software-engineering',
    label: 'Software Engineer',
    group: 'Engineering & Data',
    must: [
      'software',
      'code',
      'api',
      'testing',
      'git',
      'debug',
      'deploy',
      'algorithms',
      'data structures',
      'review',
    ],
    nice: ['ci/cd', 'docker', 'microservices', 'agile', 'scalability', 'open source', 'refactor'],
  },
  {
    id: 'frontend-engineering',
    label: 'Frontend / Web Developer',
    group: 'Engineering & Data',
    must: [
      'javascript',
      'css',
      'html',
      'react',
      'responsive',
      'component',
      'ui',
      'browser',
      'api',
      'testing',
    ],
    nice: ['typescript', 'accessibility', 'performance', 'next.js', 'figma', 'webpack', 'vite'],
  },
  {
    id: 'backend-engineering',
    label: 'Backend Engineer',
    group: 'Engineering & Data',
    must: [
      'api',
      'database',
      'sql',
      'server',
      'backend',
      'testing',
      'authentication',
      'performance',
      'caching',
      'deploy',
    ],
    nice: ['redis', 'kubernetes', 'docker', 'microservices', 'queue', 'grpc', 'load balancing'],
  },
  {
    id: 'mobile-engineering',
    label: 'Mobile Developer',
    group: 'Engineering & Data',
    must: [
      'mobile',
      'ios',
      'android',
      'app',
      'ui',
      'api',
      'testing',
      'release',
      'performance',
      'app store',
    ],
    nice: ['swift', 'kotlin', 'react native', 'flutter', 'push notifications', 'offline'],
  },
  {
    id: 'data-analytics',
    label: 'Data Analyst',
    group: 'Engineering & Data',
    must: [
      'sql',
      'excel',
      'dashboard',
      'reporting',
      'data',
      'analysis',
      'visualisation',
      'metrics',
      'stakeholder',
      'insights',
    ],
    nice: ['power bi', 'tableau', 'python', 'a/b test', 'etl', 'looker', 'forecasting'],
  },
  {
    id: 'data-science',
    label: 'Data Scientist',
    group: 'Engineering & Data',
    must: [
      'python',
      'statistics',
      'machine learning',
      'model',
      'data',
      'feature',
      'validation',
      'sql',
      'experiment',
      'pandas',
    ],
    nice: ['scikit-learn', 'deep learning', 'nlp', 'a/b test', 'mlops', 'notebook', 'regression'],
  },
  {
    id: 'machine-learning',
    label: 'ML / AI Engineer',
    group: 'Engineering & Data',
    must: [
      'machine learning',
      'model',
      'training',
      'python',
      'dataset',
      'evaluation',
      'inference',
      'pipeline',
      'accuracy',
      'deep learning',
    ],
    nice: ['pytorch', 'tensorflow', 'llm', 'transformers', 'gpu', 'mlops', 'fine-tuning'],
  },
  {
    id: 'devops',
    label: 'DevOps / Cloud / SRE',
    group: 'Engineering & Data',
    must: [
      'ci/cd',
      'cloud',
      'infrastructure',
      'automation',
      'monitoring',
      'deploy',
      'docker',
      'linux',
      'reliability',
      'pipeline',
    ],
    nice: ['kubernetes', 'terraform', 'aws', 'azure', 'observability', 'incident', 'ansible'],
  },
  {
    id: 'cybersecurity',
    label: 'Cybersecurity',
    group: 'Engineering & Data',
    must: [
      'security',
      'vulnerability',
      'threat',
      'network',
      'incident',
      'risk',
      'compliance',
      'monitoring',
      'audit',
      'access control',
    ],
    nice: ['penetration testing', 'siem', 'owasp', 'encryption', 'forensics', 'soc', 'iso 27001'],
  },
  {
    id: 'qa-engineering',
    label: 'QA / Test Engineer',
    group: 'Engineering & Data',
    must: [
      'testing',
      'test cases',
      'automation',
      'quality',
      'bug',
      'regression',
      'coverage',
      'release',
      'defect',
      'documentation',
    ],
    nice: ['selenium', 'cypress', 'playwright', 'api testing', 'performance testing', 'jira'],
  },

  /* ------------------------- Product & Design -------------------------- */
  {
    id: 'product-management',
    label: 'Product Manager',
    group: 'Product & Design',
    must: [
      'product',
      'roadmap',
      'stakeholder',
      'user',
      'requirements',
      'metrics',
      'prioritis',
      'launch',
      'research',
      'cross-functional',
    ],
    nice: ['a/b test', 'discovery', 'okr', 'backlog', 'go-to-market', 'jira', 'user story'],
  },
  {
    id: 'ux-design',
    label: 'UX / Product Designer',
    group: 'Product & Design',
    must: [
      'design',
      'user',
      'research',
      'prototype',
      'wireframe',
      'usability',
      'figma',
      'interface',
      'iteration',
      'accessibility',
    ],
    nice: ['design system', 'user testing', 'journey map', 'persona', 'interaction design'],
  },
  {
    id: 'graphic-design',
    label: 'Graphic / Visual Designer',
    group: 'Product & Design',
    must: [
      'design',
      'brand',
      'layout',
      'typography',
      'visual',
      'adobe',
      'illustration',
      'creative',
      'client',
      'asset',
    ],
    nice: ['photoshop', 'illustrator', 'after effects', 'motion', 'print', 'packaging'],
  },

  /* -------------------------- Business & Ops --------------------------- */
  {
    id: 'business-analyst',
    label: 'Business Analyst',
    group: 'Business & Operations',
    must: [
      'requirements',
      'process',
      'stakeholder',
      'analysis',
      'documentation',
      'data',
      'reporting',
      'improvement',
      'workflow',
      'excel',
    ],
    nice: ['sql', 'bpmn', 'user story', 'gap analysis', 'jira', 'visio'],
  },
  {
    id: 'digital-marketing',
    label: 'Digital Marketing',
    group: 'Business & Operations',
    must: [
      'marketing',
      'campaign',
      'content',
      'seo',
      'social media',
      'engagement',
      'analytics',
      'audience',
      'conversion',
      'brand',
    ],
    nice: ['google ads', 'email marketing', 'copywriting', 'ctr', 'roas', 'hubspot', 'crm'],
  },
  {
    id: 'sales',
    label: 'Sales / Business Development',
    group: 'Business & Operations',
    must: [
      'sales',
      'pipeline',
      'client',
      'quota',
      'revenue',
      'negotiation',
      'crm',
      'prospect',
      'relationship',
      'target',
    ],
    nice: ['salesforce', 'lead generation', 'upsell', 'account management', 'cold outreach'],
  },
  {
    id: 'finance',
    label: 'Finance / Accounting',
    group: 'Business & Operations',
    must: [
      'financial',
      'reporting',
      'analysis',
      'budget',
      'excel',
      'forecast',
      'reconciliation',
      'compliance',
      'audit',
      'variance',
    ],
    nice: ['sap', 'tally', 'gaap', 'ifrs', 'valuation', 'modelling', 'quickbooks'],
  },
  {
    id: 'consulting',
    label: 'Consulting / Strategy',
    group: 'Business & Operations',
    must: [
      'client',
      'strategy',
      'analysis',
      'recommendation',
      'stakeholder',
      'research',
      'presentation',
      'engagement',
      'framework',
      'impact',
    ],
    nice: ['market sizing', 'due diligence', 'benchmarking', 'workshop', 'business case'],
  },
  {
    id: 'human-resources',
    label: 'HR / People / Recruiting',
    group: 'Business & Operations',
    must: [
      'recruit',
      'candidate',
      'hiring',
      'onboarding',
      'employee',
      'interview',
      'talent',
      'engagement',
      'policy',
      'stakeholder',
    ],
    nice: ['ats', 'sourcing', 'employer brand', 'performance review', 'hris', 'retention'],
  },
  {
    id: 'operations',
    label: 'Operations / Supply Chain',
    group: 'Business & Operations',
    must: [
      'operations',
      'process',
      'efficiency',
      'inventory',
      'logistics',
      'vendor',
      'cost',
      'planning',
      'quality',
      'reporting',
    ],
    nice: ['lean', 'six sigma', 'erp', 'sop', 'procurement', 'kpi', 'forecasting'],
  },

  /* -------------------------- Core engineering ------------------------- */
  {
    id: 'mechanical-engineering',
    label: 'Mechanical Engineering',
    group: 'Core Engineering & Other',
    must: [
      'design',
      'cad',
      'manufacturing',
      'tolerance',
      'prototype',
      'analysis',
      'testing',
      'drawing',
      'material',
      'assembly',
    ],
    nice: ['solidworks', 'ansys', 'gd&t', 'fea', 'thermal', 'autocad', 'catia'],
  },
  {
    id: 'electrical-engineering',
    label: 'Electrical / Electronics Engineering',
    group: 'Core Engineering & Other',
    must: [
      'circuit',
      'design',
      'pcb',
      'testing',
      'power',
      'embedded',
      'signal',
      'simulation',
      'hardware',
      'troubleshoot',
    ],
    nice: ['verilog', 'matlab', 'microcontroller', 'altium', 'oscilloscope', 'firmware'],
  },
  {
    id: 'civil-engineering',
    label: 'Civil / Structural Engineering',
    group: 'Core Engineering & Other',
    must: [
      'design',
      'structural',
      'site',
      'drawing',
      'estimation',
      'construction',
      'survey',
      'safety',
      'specification',
      'inspection',
    ],
    nice: ['autocad', 'staad', 'revit', 'bim', 'concrete', 'project scheduling'],
  },
  {
    id: 'teaching',
    label: 'Teaching / Education',
    group: 'Core Engineering & Other',
    must: [
      'teaching',
      'curriculum',
      'student',
      'lesson',
      'assessment',
      'learning',
      'classroom',
      'mentor',
      'engagement',
      'feedback',
    ],
    nice: ['pedagogy', 'edtech', 'differentiated instruction', 'workshop', 'tutoring'],
  },
];

/** Looks up a track by id. Returns `null` for `''` or an unknown id. */
export function findRoleTrack(id: string): RoleTrack | null {
  if (!id) return null;
  return ROLE_TRACKS.find((track) => track.id === id) ?? null;
}

/** Track ids grouped for rendering the role picker, preserving array order. */
export function groupedRoleTracks(): { group: string; tracks: RoleTrack[] }[] {
  const groups: { group: string; tracks: RoleTrack[] }[] = [];
  for (const track of ROLE_TRACKS) {
    const existing = groups.find((g) => g.group === track.group);
    if (existing) existing.tracks.push(track);
    else groups.push({ group: track.group, tracks: [track] });
  }
  return groups;
}
